import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  Button,
  Spin,
  Modal,
  Input,
  App,
} from "antd";
import {
  LeftOutlined,
  EditOutlined,
  PlayCircleOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import theme from "../../themes";
import { pageShellCss, pageTitleCss, wrapRowCss } from "../../styles/responsive";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  getCampaignByIdThunk,
  createTestCallThunk,
  updateCampaignThunk,
} from "../../store/actions/campaign.action";
import { ReactComponent as StopCampaignIcon } from "../../assets/icons/stopCampaign.svg";
import { CAMPAIGN_STATUS, isMessagingCampaignChannel } from "./type";
import MessageCampaignDetail from "./MessageCampaignDetail";
import { IUpdateCampaign } from "../../models/campaign.model";
import ToastMessage from "../../utils/toastMessage";
import { ReactComponent as CheckCircleIcon } from "../../assets/icons/checkCircle.svg";
import { ReactComponent as PhoneIcon } from "../../assets/icons/phone.svg";
import {
  formatNumber,
  formatSecondsToTime,
} from "../../utils/formatDecimalNumber";
import * as yup from "yup";
import ScheduleCampaignModal from "../../components/ScheduleCampaignModal";
import dayjs from "dayjs";
import { TIMEZONE_CONFIG, TimezoneKey } from "../../utils/timezoneUtils";

const statusColors: Record<string, { text: string; background: string }> = {
  [CAMPAIGN_STATUS.IN_PROGRESS]: {
    text: theme.colors.green1,
    background: theme.colors.green2,
  },
  [CAMPAIGN_STATUS.TEST]: {
    text: theme.colors.yellow2,
    background: theme.colors.yellow3,
  },
  [CAMPAIGN_STATUS.SCHEDULE]: {
    text: theme.colors.blue1,
    background: theme.colors.blue2,
  },
  [CAMPAIGN_STATUS.COMPLETED]: {
    text: theme.colors.purple1,
    background: theme.colors.purple2,
  },
  [CAMPAIGN_STATUS.STOPPED]: {
    text: theme.colors.grey1,
    background: theme.colors.grey5,
  },
};

const CampaignDetail: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [campaign, setCampaign] = useState<any>(null);
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((state) => state.campaign);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const { message } = App.useApp();
  const toast = ToastMessage(message);
  const [isTestCallModalOpen, setIsTestCallModalOpen] = useState(false);
  const [testRecipientName, setTestRecipientName] = useState("");
  const [testPhoneNumber, setTestPhoneNumber] = useState("");
  const [testCustomVars, setTestCustomVars] = useState<{
    [key: string]: string;
  }>({});
  const [testCallErrors, setTestCallErrors] = useState<{
    recipientName?: string;
    phoneNumber?: string;
    customVars?: { [key: string]: string | undefined };
  }>({});
  const [isStopModalOpen, setIsStopModalOpen] = useState(false);

  // Yup schema for test call
  const testCallSchema = yup.object().shape({
    recipientName: yup.string().required(t("campaignDetail.validation.recipientNameRequired")),
    phoneNumber: yup
      .string()
      .required(t("campaignDetail.validation.phoneNumberRequired"))
      .matches(/^[0-9\-+() ]+$/, t("campaignDetail.validation.invalidPhoneNumber")),
    customVars: yup
      .object()
      .test(
        "custom-vars-validation",
        t("campaignDetail.validation.customVariablesRequired"),
        function (value: any) {
          if (!campaign?.agent_id?.parameters?.custom_variables) return true;
          const customVars = Object.values(
            campaign.agent_id.parameters.custom_variables
          ).filter(
            (varName: any): varName is string => typeof varName === "string"
          );
          return customVars.every(
            (varName: string) =>
              value && value[varName] && value[varName].trim() !== ""
          );
        }
      ),
  });

  const handleTestCall = async () => {
    try {
      await testCallSchema.validate(
        {
          recipientName: testRecipientName,
          phoneNumber: testPhoneNumber,
          customVars: testCustomVars,
        },
        { abortEarly: false }
      );

      await dispatch(
        createTestCallThunk({
          campaign_id: id || "",
          to: testPhoneNumber,
          variables: testCustomVars,
        })
      ).unwrap();
      setTestCallErrors({});
      setIsTestCallModalOpen(true);
    } catch (err: any) {
      if (err.inner) {
        const errors: {
          recipientName?: string;
          phoneNumber?: string;
          customVars?: { [key: string]: string };
        } = {};
        err.inner.forEach((e: any) => {
          if (e.path === "customVars") {
            // Handle custom variables validation errors
            if (!errors.customVars) errors.customVars = {};
            // Extract the specific custom variable that failed validation
            const customVarErrors: { [key: string]: string } = {};
            if (campaign?.agent_id?.parameters?.custom_variables) {
              Object.values(campaign.agent_id.parameters.custom_variables)
                .filter(
                  (varName: any): varName is string =>
                    typeof varName === "string"
                )
                .forEach((varName: string) => {
                  if (
                    !testCustomVars[varName] ||
                    testCustomVars[varName].trim() === ""
                  ) {
                    customVarErrors[varName] = t("campaignDetail.validation.variableRequired", { variableName: varName });
                  }
                });
            }
            errors.customVars = customVarErrors;
          } else if (e.path) {
            errors[e.path as keyof typeof errors] = e.message;
          }
        });
        setTestCallErrors(errors);
      }
    }
  };

  const resetTestCallForm = () => {
    setTestRecipientName("");
    setTestPhoneNumber("");
    setTestCustomVars({});
    setTestCallErrors({});
  };

  const handleUpdateCampaign = async (payload: IUpdateCampaign) => {
    try {
      await dispatch(updateCampaignThunk({ id: id || "", payload })).unwrap();
      fetchCampaignDetail();
    } catch (error) {
      console.error(error);
    }
  };

  const fetchCampaignDetail = async () => {
    try {
      const response = await dispatch(getCampaignByIdThunk(id || "")).unwrap();
      setCampaign(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (id) {
      fetchCampaignDetail();
    }
  }, [id]);

  // Initialize custom variables when campaign data is loaded
  useEffect(() => {
    if (campaign?.agent_id?.parameters?.custom_variables) {
      const initialCustomVars: { [key: string]: string } = {};
      Object.values(campaign.agent_id.parameters.custom_variables)
        .filter(
          (varName: any): varName is string => typeof varName === "string"
        )
        .forEach((varName: string) => {
          initialCustomVars[varName] = "";
        });
      setTestCustomVars(initialCustomVars);
    }
  }, [campaign]);

  // Memoize progress calculation for performance
  const callProgress = useMemo(() => {
    if (!campaign) return 0;
    return Math.min(
      ((campaign?.stats?.numberOfCalls ?? 0) /
        (campaign?.number_of_recipients ?? 1)) *
        100,
      100
    );
  }, [campaign]);

  // Memoize status label lookup
  const getStatusLabel = useCallback((status: string) => {
    // Handle empty or undefined status
    if (!status) {
      return t("outbound.statusLabels.allStatus");
    }
    
    const statusMap: Record<string, string> = {
      [CAMPAIGN_STATUS.ALL]: t("outbound.statusLabels.allStatus"),
      [CAMPAIGN_STATUS.SCHEDULE]: t("outbound.statusLabels.scheduled"),
      [CAMPAIGN_STATUS.IN_PROGRESS]: t("outbound.statusLabels.inProgress"),
      [CAMPAIGN_STATUS.STOPPED]: t("outbound.statusLabels.stopped"),
      [CAMPAIGN_STATUS.COMPLETED]: t("outbound.statusLabels.finished"),
      [CAMPAIGN_STATUS.TEST]: t("outbound.statusLabels.testing"),
    };
    
    return statusMap[status] || status;
  }, [t]);

  // Extract Stop Campaign Modal for clarity
  const renderStopModal = () => (
    <Modal
      open={isStopModalOpen}
      onCancel={() => setIsStopModalOpen(false)}
      footer={null}
      width="min(400px, calc(100vw - 24px))"
      closeIcon={<CloseOutlined style={{ fontSize: 14, color: "#000" }} />}
      styles={{
        body: {
          display: "flex",
          flexDirection: "column",
          gap: 16,
        },
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 16 }}>{t("campaignDetail.stopCampaign")}</div>
      <div style={{ fontSize: 16, fontWeight: 400 }}>
        {t("campaignDetail.areYouSureStopCampaign", { campaignName: campaign?.campaign_name })}
        <br />
        {t("campaignDetail.onceStoppedCannotResume")}
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
        <Button
          type="text"
          style={{
            fontSize: 14,
            fontWeight: 400,
            border: "none",
            background: "none",
            boxShadow: "none",
            color: "#000",
            borderRadius: 12,
          }}
          size="middle"
          onClick={async () => {
            await handleUpdateCampaign({
              status: CAMPAIGN_STATUS.STOPPED,
            });
            toast.success(
              t("campaignDetail.campaignStoppedSuccessfully"),
              5,
              <CheckCircleIcon />
            );
            setIsStopModalOpen(false);
          }}
        >
          {t("campaignDetail.stopAnyway")}
        </Button>
        <Button
          size="middle"
          style={{
            fontSize: 14,
            fontWeight: 400,
            background: theme.colors.grey5,
            color: "#000",
            border: "none",
            borderRadius: 8,
            boxShadow: "none",
          }}
          onClick={() => setIsStopModalOpen(false)}
        >
          {t("campaignDetail.cancel")}
        </Button>
      </div>
    </Modal>
  );

  // Extract Test Call Modal for clarity
  const renderTestCallModal = () => (
    <Modal
      open={isTestCallModalOpen}
      onCancel={() => {
        setIsTestCallModalOpen(false);
        resetTestCallForm();
      }}
      footer={null}
      width="min(520px, calc(100vw - 24px))"
      closeIcon={<CloseOutlined style={{ fontSize: 16, color: "#000" }} />}
      styles={{
        body: {
          display: "flex",
          flexDirection: "column",
          gap: 16,
        },
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 18 }}>{t("campaignDetail.testCall")}</div>
      <div style={{ fontSize: 16 }}>
        {t("campaignDetail.testCallInProgress", {
          agentName: campaign?.agent_id?.agent_name || "Agent",
          phoneNumber: testPhoneNumber
        })}
      </div>
    </Modal>
  );

  if (!loading && campaign && isMessagingCampaignChannel(campaign.channel)) {
    return <MessageCampaignDetail initialCampaign={campaign} />;
  }

  return (
    <FlexRow>
      {loading && (
        <LoadingOverlay>
          <Spin size="default" />
        </LoadingOverlay>
      )}
      {!loading && (
        <MainContent>
          <BackLink onClick={() => navigate(-1)}>
            <LeftOutlined style={{ marginRight: 8 }} /> {t("campaignDetail.backToCampaigns")}
          </BackLink>

          <HeaderRow>
            <Title>
              {campaign?.campaign_name || t("campaignDetail.details.campaignDetails")}
              {campaign && (
                <StatusBadge colors={statusColors[campaign.status]}>
                  {getStatusLabel(campaign.status)}
                </StatusBadge>
              )}
            </Title>
            {campaign &&
              (() => {
                const status = (campaign.status || "").toLowerCase();
                if (
                  status === CAMPAIGN_STATUS.STOPPED ||
                  status === CAMPAIGN_STATUS.COMPLETED
                ) {
                  return (
                    <Button
                      size="large"
                      type="default"
                      onClick={() =>
                        navigate(`/reports/${campaign._id}`, {
                          state: {
                            campaignName: campaign.campaign_name,
                          },
                        })
                      }
                    >
                      {t("campaignDetail.viewReport")}
                    </Button>
                  );
                }
                // Show only Edit Schedule Time when status is TEST
                if (status === CAMPAIGN_STATUS.TEST) {
                  return (
                    <Button
                      size="large"
                      type="default"
                      icon={<EditOutlined style={{ marginRight: 4 }} />}
                      onClick={() => setIsScheduleModalOpen(true)}
                    >
                      {t("campaignDetail.editScheduleTime")}
                    </Button>
                  );
                }
                if (status === CAMPAIGN_STATUS.SCHEDULE) {
                  return (
                    <div>
                      <Button
                        size="large"
                        type="default"
                        icon={<EditOutlined style={{ marginRight: 4 }} />}
                        style={{ marginRight: 12 }}
                        onClick={() => setIsScheduleModalOpen(true)}
                      >
                        {t("campaignDetail.editScheduleTime")}
                      </Button>
                      <Button
                        size="large"
                        type="default"
                        icon={<PlayCircleOutlined style={{ marginRight: 4 }} />}
                        onClick={() => {
                          handleUpdateCampaign({
                            status: CAMPAIGN_STATUS.IN_PROGRESS,
                          });
                          toast.success(
                            t("campaignDetail.campaignStartedSuccessfully"),
                            5,
                            <CheckCircleIcon />
                          );
                        }}
                      >
                        {t("campaignDetail.startCampaign")}
                      </Button>
                    </div>
                  );
                }
                if (status === CAMPAIGN_STATUS.IN_PROGRESS) {
                  return (
                    <>
                      <Button
                        size="large"
                        type="default"
                        icon={<StopCampaignIcon style={{ marginRight: 4 }} />}
                        onClick={() => setIsStopModalOpen(true)}
                      >
                        {t("campaignDetail.stopCampaign")}
                      </Button>
                      {renderStopModal()}
                    </>
                  );
                }
                return null;
              })()}
          </HeaderRow>

          {campaign &&
            campaign.status !== CAMPAIGN_STATUS.TEST &&
            campaign.status !== CAMPAIGN_STATUS.SCHEDULE && (
              <CardFlexRow>
                <CardBoxCalls>
                  <CardTitle>{t("campaignDetail.calls")}</CardTitle>
                  <CallCount>
                    {campaign.stats?.number_of_calls || 0}
                    <span style={{ color: "#000" }}>{t("campaignDetail.called")}</span>
                    <span> / </span>
                    <span>{campaign.stats?.total_recipient || 0}</span>
                    <span>{t("campaignDetail.totalRecipients")}</span>
                  </CallCount>
                  <ProgressBar>
                    <ProgressFill
                      style={{
                        width: `${callProgress}%`,
                      }}
                    />
                  </ProgressBar>
                </CardBoxCalls>
                <CardBoxStats>
                  <CardTitleStats>{t("campaignDetail.statistics")}</CardTitleStats>
                  <StatGrid>
                    <StatItem>
                      <StatValue>
                        {campaign.stats?.answered_calls || 0}
                      </StatValue>
                      <StatLabel>{t("campaignDetail.answered")}</StatLabel>
                    </StatItem>
                    <StatItem>
                      <StatValue>
                        {formatNumber(
                          (campaign.stats?.completion_rate || 0) * 100
                        )}
                        %
                      </StatValue>
                      <StatLabel>{t("campaignDetail.answerRate")}</StatLabel>
                    </StatItem>
                    <StatItem>
                      <StatValue>
                        {formatSecondsToTime(
                          campaign.stats?.total_seconds /
                            campaign.stats?.number_of_calls || 0
                        )}
                      </StatValue>
                      <StatLabel>{t("campaignDetail.avgDuration")}</StatLabel>
                    </StatItem>
                  </StatGrid>
                </CardBoxStats>
              </CardFlexRow>
            )}

          <DetailsCard>
            <DetailsTitle>{t("campaignDetail.details.campaignDetails")}</DetailsTitle>
            <DetailsGrid>
              <DetailLabel>{t("campaignDetail.details.phoneNumber")}</DetailLabel>
              <DetailValue>{campaign?.phone_number || "-"}</DetailValue>
              <DetailLabel>{t("campaignDetail.details.agent")}</DetailLabel>
              <DetailValue>{campaign?.agent_id?.agent_name || "-"}</DetailValue>
              <DetailLabel>{t("campaignDetail.details.numberOfRecipients")}</DetailLabel>
              <DetailValue>{campaign?.number_of_recipients || "-"}</DetailValue>
              <DetailLabel>{t("campaignDetail.details.recipientFile")}</DetailLabel>
              <DetailValue>
                {campaign?.audience_list_id ? (
                  <RecipientFileLink
                    href={campaign.audience_list_id.file}
                    download
                    rel="noopener noreferrer"
                  >
                    {campaign.audience_list_id.audience_list_name}
                  </RecipientFileLink>
                ) : (
                  "-"
                )}
              </DetailValue>
              {/* <DetailLabel>{t("campaignDetail.details.language")}</DetailLabel>
              <DetailValue>{campaign?.agent_id?.language || "-"}</DetailValue> */}
              <DetailLabel>{t("campaignDetail.details.scheduledTime")}</DetailLabel>
              <DetailValue>
                {campaign?.start_at
                  ? dayjs
                      .utc(campaign?.start_at)
                      .tz(TIMEZONE_CONFIG[campaign?.time_zone as TimezoneKey])
                      .format("MMM DD, YYYY hh:mm A")
                  : "-"}
              </DetailValue>
            </DetailsGrid>
          </DetailsCard>

          {/* Test Call Section for TEST status */}
          {campaign && campaign.status === CAMPAIGN_STATUS.TEST && (
            <TestCallCard>
              <TestCallTitle>{t("campaignDetail.testCall")}</TestCallTitle>
              <TestCallGrid>
                <div>{t("campaignDetail.testRecipientName")}</div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <StyledAntdInput
                    allowClear
                    size="large"
                    placeholder={t("campaignDetail.testRecipientName")}
                    value={testRecipientName}
                    onChange={(e) => {
                      setTestRecipientName(e.target.value);
                      if (testCallErrors.recipientName) {
                        setTestCallErrors((prev) => ({
                          ...prev,
                          recipientName: undefined,
                        }));
                      }
                    }}
                    status={testCallErrors.recipientName ? "error" : ""}
                  />
                  {testCallErrors.recipientName && (
                    <div style={{ color: "red", fontSize: 13, marginTop: 2 }}>
                      {testCallErrors.recipientName}
                    </div>
                  )}
                </div>
                <div>{t("campaignDetail.testPhoneNumber")}</div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <StyledAntdInput
                    allowClear
                    size="large"
                    placeholder={t("campaignDetail.testPhoneNumber")}
                    value={testPhoneNumber}
                    onChange={(e) => {
                      setTestPhoneNumber(e.target.value);
                      if (testCallErrors.phoneNumber) {
                        setTestCallErrors((prev) => ({
                          ...prev,
                          phoneNumber: undefined,
                        }));
                      }
                    }}
                    status={testCallErrors.phoneNumber ? "error" : ""}
                  />
                  {testCallErrors.phoneNumber && (
                    <div style={{ color: "red", fontSize: 13, marginTop: 2 }}>
                      {testCallErrors.phoneNumber}
                    </div>
                  )}
                </div>
                {campaign?.agent_id?.parameters?.custom_variables &&
                  Object.keys(campaign.agent_id.parameters.custom_variables)
                    .length > 0 && (
                    <>
                      {Object.values(
                        campaign.agent_id.parameters.custom_variables
                      )
                        .filter(
                          (customVar: any): customVar is string =>
                            typeof customVar === "string"
                        )
                        .map((customVar: string, index: number) => (
                          <div key={index} style={{ display: "contents" }}>
                            <div>{customVar}</div>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              <StyledAntdInput
                                allowClear
                                size="large"
                                placeholder={t("campaignDetail.enterVariable", { variableName: customVar })}
                                value={testCustomVars[customVar] || ""}
                                onChange={(e) => {
                                  setTestCustomVars((prev) => ({
                                    ...prev,
                                    [customVar]: e.target.value,
                                  }));
                                  if (testCallErrors.customVars?.[customVar]) {
                                    setTestCallErrors((prev) => ({
                                      ...prev,
                                      customVars: {
                                        ...(prev.customVars || {}),
                                        [customVar]: undefined,
                                      },
                                    }));
                                  }
                                }}
                                status={
                                  testCallErrors.customVars?.[customVar]
                                    ? "error"
                                    : ""
                                }
                              />
                              {testCallErrors.customVars?.[customVar] && (
                                <div
                                  style={{
                                    color: "red",
                                    fontSize: 13,
                                    marginTop: 2,
                                  }}
                                >
                                  {testCallErrors.customVars[customVar]}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                    </>
                  )}
                {/* <div>{t("campaignDetail.details.language")}</div>
                <div style={{ fontSize: 15 }}>English</div> */}
              </TestCallGrid>
              <div
                style={{
                  marginTop: 32,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <TestCallButton type="primary" onClick={handleTestCall}>
                  <span role="img" aria-label="call" style={{ fontSize: 18 }}>
                    <PhoneIcon />
                  </span>{" "}
                  {t("campaignDetail.makeTestCall")}
                </TestCallButton>
              </div>
              {renderTestCallModal()}
            </TestCallCard>
          )}

          {/* Schedule Campaign Time Modal */}
          <ScheduleCampaignModal
            open={isScheduleModalOpen}
            onCancel={() => setIsScheduleModalOpen(false)}
            onSchedule={async (utcStartAt: string, tz: string) => {
              if (!utcStartAt) {
                toast.error(t("campaignDetail.pleaseSelectDateAndTime"));
                return;
              }
              try {
                await handleUpdateCampaign({
                  start_at: utcStartAt,
                  time_zone: tz,
                  status: CAMPAIGN_STATUS.SCHEDULE,
                });
                toast.success(
                  t("outbound.scheduleUpdatedSuccessfully"),
                  5,
                  <CheckCircleIcon />
                );
                setIsScheduleModalOpen(false);
              } catch (error) {
                toast.error(t("outbound.failedToUpdateSchedule"));
              }
            }}
          />
        </MainContent>
      )}
    </FlexRow>
  );
};

const FlexRow = styled.div`
  display: flex;
  min-height: 100%;
  background-color: ${theme.colors.grey3};
`;

const MainContent = styled.div`
  flex: 1;
  min-width: 0;
  gap: 24px;
  display: flex;
  flex-direction: column;
  ${pageShellCss}
`;

const BackLink = styled.div`
  color: ${theme.colors.blue1};
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
`;

const HeaderRow = styled.div`
  ${wrapRowCss}
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  ${pageTitleCss}
`;

const StatusBadge = styled.span<{
  colors: { text: string; background: string };
}>`
  background: ${({ colors }) => colors.background};
  color: ${({ colors }) => colors.text};
  border-radius: 12px;
  padding: 4px 12px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
`;

const CardBox = styled(Card)`
  min-width: 0;
  border-radius: 12px;
  border: 1px solid ${theme.colors.grey2};
`;

const CardTitle = styled.div`
  font-size: 16px;
  color: ${theme.colors.grey1};
  margin-bottom: 16px;
`;

const CallCount = styled.div`
  font-size: 24px;
  margin-bottom: 8px;
  span {
    font-size: 16px;
    font-weight: 400;
    color: ${theme.colors.grey1};
    margin-left: 4px;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: ${theme.colors.grey2};
  border-radius: 8px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${theme.colors.blue1};
  border-radius: 4px;
  transition: width 0.3s ease;
`;

const StatGrid = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
  gap: 12px;
  flex-wrap: wrap;
`;

const StatItem = styled.div``;

const StatValue = styled.span`
  font-size: 24px;
`;

const StatLabel = styled.span`
  font-size: 16px;
  color: ${theme.colors.grey1};
  margin-left: 4px;
`;

const DetailsCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${theme.colors.grey2};
`;

const DetailsTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${theme.colors.grey1};
  margin-bottom: 24px;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  row-gap: 24px;
  column-gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    row-gap: 8px;
  }
`;

const DetailLabel = styled.div`
  color: ${theme.colors.grey1};
  font-size: 15px;
  font-weight: 500;
`;

const DetailValue = styled.div`
  font-size: 15px;
  font-weight: 400;
`;

// Add styled components for the flex row and card boxes
const CardFlexRow = styled.div`
  display: flex;
  gap: 24px;
  width: 100%;
  min-width: 0;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const CardBoxCalls = styled(CardBox)`
  flex: 1;
  min-width: 0;
  margin-right: 0;
`;

const CardBoxStats = styled(CardBox)`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;

  .ant-card-body {
    width: 100%;
    height: 100%;
  }
`;

const CardTitleStats = styled(CardTitle)`
  align-self: flex-start;
`;

const RecipientFileLink = styled.a`
  color: ${theme.colors.blue1};
`;

const SubmitButton = styled(Button)`
  background: ${theme.colors.green1};
  color: #fff;
  border: none;
  border-radius: 8px;
  height: 40px;
  padding: 0 16px;
  font-size: 16px;
  font-weight: 400;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #047857 !important;
  }
`;

const CancelButton = styled(Button)`
  background: ${theme.colors.grey3};
  color: #000;
  border: 1px solid ${theme.colors.grey2};
  border-radius: 8px;
  height: 40px;

  &:hover {
    border: 1px solid ${theme.colors.grey2} !important;
    background: ${theme.colors.grey3} !important;
    color: #000 !important;
  }
`;

const TestCallCard = styled(Card)`
  border-radius: 12px;
`;

const TestCallTitle = styled.div`
  font-size: 18px;
  font-weight: 500;
  color: #8c98a4;
  margin-bottom: 24px;
`;

const TestCallGrid = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  row-gap: 24px;
  column-gap: 24px;
  min-width: 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    row-gap: 8px;
  }
`;

const StyledAntdInput = styled(Input)`
  width: 320px;
  max-width: 100%;
  border-radius: 8px;
  font-size: 15px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const TestCallButton = styled(Button)`
  background: ${theme.colors.green1};
  border-radius: 8px;
  height: 40px;
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #fff;
  border: none;
  &:hover {
    background: ${theme.colors.green1} !important;
    color: #fff !important;
  }
`;

const LoadingOverlay = styled.div`
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  width: 100%;
`;

export default CampaignDetail;
